import React, { useMemo, useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { motion } from "framer-motion";
import clsx from "clsx";
import { useIntl } from "react-intl";
import "./BenefitsDonutCharts.less";

type Slice = {
   label: string;
   value: number;
   color: string;
   isRemainder?: boolean;
};

type ChartConfig = {
   title: string;
   total: number;
   slices: Slice[];
};

type Props = {
   config: ChartConfig;
   className?: string;
};

const OUTER_RADIUS = "90%";
const INNER_RADIUS = "55%";
const LABEL_PERCENT_THRESHOLD = 0.1;

const toFiniteNumber = (value: unknown): number => {
   if (typeof value !== "number") return 0;
   if (!Number.isFinite(value)) return 0;
   return value;
};

const getContrastColor = (hex: string): string => {
   const clean = hex.replace("#", "");
   const r = parseInt(clean.substring(0, 2), 16) || 0;
   const g = parseInt(clean.substring(2, 4), 16) || 0;
   const b = parseInt(clean.substring(4, 6), 16) || 0;
   return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55 ? "#333" : "#fff";
};

const RADIAN = Math.PI / 180;

const renderSegmentLabel = (props: any) => {
   const { cx, cy, midAngle, innerRadius, outerRadius, value, percent, fill, index, payload } =
      props;
   if (percent < 0.01) return null;
   if (payload?.isRemainder) return null;

   if (percent >= LABEL_PERCENT_THRESHOLD) {
      const radius = (innerRadius + outerRadius) / 2;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);
      const textColor = getContrastColor(fill || "#888");
      const strokeColor = textColor === "#fff" ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.5)";

      return (
         <text
            x={x}
            y={y}
            fill={textColor}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={15}
            fontWeight={700}
            paintOrder="stroke"
            stroke={strokeColor}
            strokeWidth={2}
            strokeLinejoin="round"
         >
            {value}
         </text>
      );
   }

   const cos = Math.cos(-midAngle * RADIAN);
   const sin = Math.sin(-midAngle * RADIAN);
   const sx = cx + outerRadius * cos;
   const sy = cy + outerRadius * sin;
   const mx = cx + (outerRadius + 14) * cos;
   const my = cy + (outerRadius + 14) * sin;
   const ex = mx + (cos >= 0 ? 12 : -12);
   const ey = my;

   return (
      <g>
         <path
            d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
            stroke="#999"
            strokeWidth={1}
            fill="none"
         />
         <circle cx={sx} cy={sy} r={2} fill="#999" />
         <text
            x={ex + (cos >= 0 ? 4 : -4)}
            y={ey}
            fill="#666"
            textAnchor={cos >= 0 ? "start" : "end"}
            dominantBaseline="central"
            fontSize={13}
            fontWeight={600}
         >
            {value}
         </text>
      </g>
   );
};

export const DonutChart: React.FC<Props> = ({ config, className }) => {
   const intl = useIntl();
   const { title, slices, total } = config;
   const [activeIndex, setActiveIndex] = useState<number | null>(null);
   const [animationDone, setAnimationDone] = useState(false);

   useEffect(() => {
      const timer = setTimeout(() => setAnimationDone(true), 850);
      return () => clearTimeout(timer);
   }, []);

   const data = useMemo(() => {
      const mapped = slices.map((s) => ({
         ...s,
         value: toFiniteNumber(s.value),
      }));

      const providedTotal = Math.max(0, toFiniteNumber(total));
      if (providedTotal > 0) {
         const sum = mapped.reduce((acc, curr) => acc + curr.value, 0);
         const remainder = providedTotal - sum;
         if (remainder > 0) {
            mapped.push({
               label: intl.formatMessage({
                  id: "benefitsChart.remaining",
                  defaultMessage: "Pozostałe",
               }),
               value: remainder,
               color: "#E6EEF6",
               isRemainder: true,
            });
         }
      }

      return mapped;
   }, [slices, total, intl]);

   const totalValue = useMemo(() => {
      const providedTotal = Math.max(0, toFiniteNumber(total));
      if (providedTotal > 0) return providedTotal;
      return data.reduce((acc, curr) => acc + curr.value, 0);
   }, [total, data]);

   const isEmpty = totalValue === 0 || data.length === 0;

   const onPieEnter = (_: any, index: number) => {
      setActiveIndex(index);
   };

   const onPieLeave = () => {
      setActiveIndex(null);
   };

   return (
      <div className={clsx("donut-chart", className)}>
         <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="donut-chart__container"
         >
            <div className="donut-chart__header">
               <h3 className="donut-chart__title">{title}</h3>
            </div>

            <div className="donut-chart__content">
               <div className="donut-chart__visual">
                  <ResponsiveContainer width="100%" height="100%">
                     <PieChart>
                        {!isEmpty && (
                           <Pie
                              data={data}
                              dataKey="value"
                              nameKey="label"
                              innerRadius={INNER_RADIUS}
                              outerRadius={OUTER_RADIUS}
                              paddingAngle={data.length > 1 ? 2 : 0}
                              cornerRadius={4}
                              stroke="none"
                              label={renderSegmentLabel}
                              labelLine={false}
                              onMouseEnter={onPieEnter}
                              onMouseLeave={onPieLeave}
                              animationBegin={0}
                              animationDuration={800}
                              animationEasing="ease-out"
                              isAnimationActive={!animationDone}
                           >
                              {data.map((entry, index) => (
                                 <Cell
                                    key={`cell-${index}`}
                                    fill={entry.color}
                                    style={{
                                       cursor: "pointer",
                                       transition: "opacity 0.2s ease",
                                    }}
                                    opacity={
                                       activeIndex === null || activeIndex === index ? 1 : 0.4
                                    }
                                 />
                              ))}
                           </Pie>
                        )}
                     </PieChart>
                  </ResponsiveContainer>

                  <div className="donut-chart__center">
                     <div className="donut-chart__center-content">
                        <span className="donut-chart__center-value">{totalValue}</span>
                        <span className="donut-chart__center-label">
                           {intl.formatMessage({
                              id: "benefitsChart.total",
                              defaultMessage: "TOTAL",
                           })}
                        </span>
                     </div>
                  </div>
               </div>

               <div className="donut-chart__legend">
                  {[...data]
                     .filter((item) => !item.isRemainder)
                     .sort((a, b) => b.value - a.value)
                     .map((item, index) => (
                        <div
                           key={index}
                           className={clsx("donut-chart__legend-item", {
                              "donut-chart__legend-item--active": activeIndex === index,
                              "donut-chart__legend-item--dimmed":
                                 activeIndex !== null && activeIndex !== index,
                           })}
                           onMouseEnter={() => setActiveIndex(index)}
                           onMouseLeave={() => setActiveIndex(null)}
                        >
                           <span
                              className="donut-chart__legend-dot"
                              style={{ backgroundColor: item.color }}
                           />
                           <span className="donut-chart__legend-text">{item.label}</span>
                           <span className="donut-chart__legend-value">{item.value}</span>
                        </div>
                     ))}
               </div>
            </div>
         </motion.div>
      </div>
   );
};

export const DonutSkeleton = () => (
   <div className="donut-chart skeleton">
      <div className="donut-chart__container">
         <div className="donut-chart__header">
            <div className="skeleton-item skeleton-title" />
         </div>
         <div className="donut-chart__content">
            <div className="donut-chart__visual">
               <div className="skeleton-item skeleton-circle" />
            </div>
            <div className="donut-chart__legend">
               <div className="skeleton-item skeleton-legend" />
               <div className="skeleton-item skeleton-legend" />
               <div className="skeleton-item skeleton-legend" />
            </div>
         </div>
      </div>
   </div>
);

export const BenefitsDonutCharts: React.FC<{ left: ChartConfig; right: ChartConfig }> = ({
   left,
   right,
}) => (
   <div className="benefits-donuts-redesign">
      <DonutChart config={left} />
      {right.slices.length > 0 && <DonutChart config={right} />}
   </div>
);
